import { all, call, put, takeLatest } from 'redux-saga/effects';
import { fetchCoursesList, fetchCourseDetail, activateCourse } from '../api/coursesApi.js';
import {
  coursesRequested,
  coursesLoaded,
  courseMediaSettingsLoaded,
  coursesFailed,
} from './coursesListSlice.js';
import { courseDetailRequested, courseDetailLoaded, courseDetailFailed } from './courseDetailSlice.js';
import { captureSagaError } from '../../../lib/sentry.js';
import { friendlyErrorMessage } from '../../../lib/errorCopy.js';
import { extractMediaSettings } from '../lib/mediaSettings.js';
import { isAssessmentCourse } from '../lib/courseClassification.js';

// The list endpoint and the detail endpoint use DIFFERENT field names for
// the same data. List: course_title / course_domain / percentcomplete.
// Detail (nested under coursedetails): title / domain / progressbar.
// Normalize the list items to the detail-style names so CourseCard (and any
// other consumer) doesn't have to know which endpoint produced its data.
function normalizeCourseListItem(item) {
  return {
    ...item,
    image_url: item.image_url ?? '',
    title: item.course_title ?? item.title ?? item.course_name ?? '',
    domain: item.course_domain ?? item.domain ?? '',
    progressbar: item.percentcomplete ?? item.progressbar ?? 0,
    // Backend may send either the additive is_assessment flag or
    // course_type: "Assessment"; normalize both into the boolean the UI reads.
    is_assessment: isAssessmentCourse(item),
  };
}

// Course elements inside coursedetails.units use `elementID` (capital ID, the
// API's quirk) and `completion_count` / `total_attempts` rather than the
// snake_case names the components destructure. Normalize so CourseElementRow
// can read element.element_id, .attempts_completed, .totalnumberofattempts.
// Without this fix the Start link became /learn/:courseId/undefined and the
// saga's startAttempt failed.
function normalizeElement(el) {
  return {
    ...el,
    element_id: el.element_id ?? el.elementID ?? null,
    attempts_completed: el.attempts_completed ?? el.completion_count ?? el.completed ?? 0,
    totalnumberofattempts: el.totalnumberofattempts ?? el.total_attempts ?? el.possible ?? 1,
    percentcomplete: el.percentcomplete ?? el.percent_complete ?? el.progressbar ?? null,
  };
}

function normalizeUnits(units) {
  if (!units || typeof units !== 'object') return {};
  const out = {};
  for (const [unitId, unit] of Object.entries(units)) {
    out[unitId] = {
      ...unit,
      course_elements: Array.isArray(unit.course_elements)
        ? unit.course_elements.map(normalizeElement)
        : [],
    };
  }
  return out;
}

// Switches server-side active course alongside the detail fetch. Failures
// are captured to Sentry and swallowed — a hypothetical activate failure
// must not break course-detail render (users only reach purchased
// non-expired courses, so the failure path is defensive only).
function* safeActivate(courseId) {
  try {
    yield call(activateCourse, courseId);
  } catch (err) {
    captureSagaError(err, { action: 'coursesSaga/activateCourse', courseId });
  }
}

export function* handleFetchCoursesList() {
  try {
    const res = yield call(fetchCoursesList);
    // /v1/courses returns `status: "success"` (string); other endpoints use `true`.
    // Truthy + code 200 covers both.
    if (res && res.status && res.code === 200) {
      const items = Array.isArray(res.data) ? res.data.map(normalizeCourseListItem) : [];
      yield put(coursesLoaded(items));
      const mediaSettings = extractMediaSettings(res);
      if (mediaSettings) yield put(courseMediaSettingsLoaded(mediaSettings));
    } else {
      yield put(coursesFailed(res?.message || friendlyErrorMessage(null, 'Failed to load courses')));
    }
  } catch (err) {
    captureSagaError(err, { action: 'coursesList/coursesRequested' });
    yield put(coursesFailed(friendlyErrorMessage(err, 'Failed to load courses')));
  }
}

export function* handleFetchCourseDetail(action) {
  try {
    const courseId = action.payload;
    const [, res] = yield all([
      call(safeActivate, courseId),
      call(fetchCourseDetail, courseId),
    ]);
    if (res && res.status && res.code === 200) {
      const { coursedetails, continue: continueEl, current_unit } = res.data;
      yield put(courseDetailLoaded({
        courseId: String(coursedetails.course_id),
        title: coursedetails.title ?? '',
        imageUrl: coursedetails.image_url ?? '',
        domain: coursedetails.domain ?? '',
        category: coursedetails.category ?? '',
        progressPercent: coursedetails.progressbar ?? 0,
        units: normalizeUnits(coursedetails.units),
        continueElement: continueEl ?? null,
        currentUnitId: current_unit ? String(current_unit) : null,
      }));
      const mediaSettings = extractMediaSettings(res);
      if (mediaSettings) yield put(courseMediaSettingsLoaded(mediaSettings));
    } else {
      yield put(courseDetailFailed(res?.message || 'Failed to load course'));
    }
  } catch (err) {
    captureSagaError(err, { action: 'courseDetail/courseDetailRequested' });
    yield put(courseDetailFailed(err?.message || 'Failed to load course'));
  }
}

export default function* coursesSaga() {
  yield takeLatest(coursesRequested.type, handleFetchCoursesList);
  yield takeLatest(courseDetailRequested.type, handleFetchCourseDetail);
}
